import { ComponentTypes, type All8PageContent } from "@bcc/core-renderers";
import { createComponentInstance, resolveComponentDefinition } from "../../Pages/pageComponentLibrary";

const CONTAINER_TYPE = ComponentTypes.LayoutSection;

/**
 * Wrap the component at `index` into a new Container (LayoutSection) that holds
 * it as its sole child. No-op (returns the same array) when the index is out of
 * range or the target is already a Container (nesting is unsupported).
 */
export function wrapComponentInContainer(components: All8PageContent, index: number): All8PageContent {
	const src: any = components[index];
	if (!src || src.componentType === CONTAINER_TYPE) return components;
	const def = resolveComponentDefinition(CONTAINER_TYPE);
	const container: any = def
		? createComponentInstance(def)
		: { componentType: CONTAINER_TYPE, content: { children: [], gap: "md", align: "start" } };
	const content = (container.content ?? {}) as Record<string, any>;
	content.children = [{ key: `col-${Date.now()}-0`, componentType: src.componentType, content: src.content }];
	container.content = content;
	const next: any[] = [...components];
	next[index] = container;
	return next as All8PageContent;
}

/**
 * Lift a Container's children back out to `index` and remove the Container.
 * No-op when the index is out of range or the target is not a Container. The
 * children's Container-only fields (`span`/`visible`/`key`) are dropped; the
 * caller's `normalizeComponents` re-derives runtime keys on the next read.
 */
export function unwrapContainerAt(components: All8PageContent, index: number): All8PageContent {
	const container: any = components[index];
	if (!container || container.componentType !== CONTAINER_TYPE) return components;
	const children: any[] = Array.isArray(container.content?.children) ? container.content.children : [];
	const lifted = children.map((c) => ({ componentType: c.componentType, content: c.content }));
	const next: any[] = [...components];
	next.splice(index, 1, ...lifted);
	return next as All8PageContent;
}
